import React, { useRef, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SearchIcon from "@mui/icons-material/Search";
import APICompanyMatchProfile from "../../types/APICompanyMatchProfile";
import { HOSTNAME_INPUT_PATTERN } from "../../constants";

const DEFAULT_PROFILE = { label: "TrueBiz Default", id: "" };

interface AutocompleteValue {
  label: string;
  id: string;
}

export default function Form({
  onSubmitSearch,
  loading,
  matchProfiles,
  selectedMatchProfileId,
  onMatchProfileChange,
  disabled,
}: {
  onSubmitSearch: (domain: string) => void;
  loading: boolean;
  matchProfiles: APICompanyMatchProfile[];
  selectedMatchProfileId: string | null;
  onMatchProfileChange: (id: string | null) => void;
  disabled: boolean;
}) {
  const [domainValue, setDomainValue] = useState<string>("");

  const { domain } = useParams();
  const navigate = useNavigate();

  // We want to fill in the form with the domain value from the URL,
  // but don't want to actually submit the API request until the user
  // presses the "Show attributes" button. This effect syncs the URL
  // state with the locally-controlled input state.
  useEffect(() => {
    if (domain) setDomainValue(decodeURIComponent(domain));
  }, [domain]);

  const searchInputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (searchInputRef.current && !domainValue) {
      searchInputRef.current.focus();
    }
  }, [domainValue]);

  const onSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();

    onSubmitSearch(domainValue);
    navigate(`/domain/${encodeURIComponent(domainValue)}`);
  };

  const displayMatchProfiles = !!matchProfiles && matchProfiles.length > 1;
  const defaultMatchProfile =
    !!matchProfiles && matchProfiles.length === 1
      ? { label: matchProfiles[0].name, id: matchProfiles[0].id }
      : DEFAULT_PROFILE;
  const matchProfileChoices =
    !!matchProfiles && matchProfiles.length <= 1
      ? [defaultMatchProfile]
      : [DEFAULT_PROFILE].concat(
          matchProfiles.map((matchProfile) => {
            return { label: matchProfile.name, id: matchProfile.id };
          })
        );

  return (
    <form onSubmit={onSubmitForm}>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={displayMatchProfiles ? 6 : 9}>
          <TextField
            fullWidth
            required
            inputRef={searchInputRef}
            disabled={loading}
            label="Domain Name"
            type="text"
            value={domainValue}
            onChange={(e) => setDomainValue(e.target.value)}
            inputProps={{
              title: "Enter a valid domain name (e.g. example.com)",
              pattern: HOSTNAME_INPUT_PATTERN,
            }}
          />
        </Grid>

        {displayMatchProfiles && (
          <Grid item xs={3} display="flex">
            <Autocomplete
              sx={{ width: "100%" }}
              disableClearable
              isOptionEqualToValue={(
                option: AutocompleteValue,
                value: AutocompleteValue
              ) => option.id === value.id}
              value={getMatchingProfile({
                profileId: selectedMatchProfileId,
                profiles: matchProfiles,
                defaultValue: defaultMatchProfile,
              })}
              disabled={matchProfiles && matchProfiles.length === 0}
              options={matchProfileChoices}
              renderInput={(params) => (
                <TextField {...params} label="Risk Profile" />
              )}
              onChange={(event, newValue) => {
                const profile = getMatchingProfile({
                  profileId: newValue?.id || null,
                  profiles: matchProfiles,
                  defaultValue: defaultMatchProfile,
                });
                onMatchProfileChange(profile.id || null);
              }}
            />
          </Grid>
        )}

        <Grid
          item
          xs={3}
          alignItems="stretch"
          justifyContent="stretch"
          display="flex"
        >
          <LoadingButton
            variant="contained"
            startIcon={<SearchIcon />}
            sx={{ width: "100%" }}
            size="large"
            disabled={loading || disabled}
            loading={loading}
            type="submit"
          >
            Submit Search
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
}

function getMatchingProfile({
  profileId,
  profiles,
  defaultValue,
}: {
  profileId: string | null;
  profiles: APICompanyMatchProfile[];
  defaultValue: AutocompleteValue;
}): AutocompleteValue {
  if (!profileId) {
    return defaultValue;
  }

  const matchingProfile = profiles.find((matchProfile) => {
    return matchProfile.id === profileId;
  });
  return matchingProfile
    ? { label: matchingProfile.name, id: matchingProfile.id }
    : defaultValue;
}
